import * as React from "react";
import {
	Box,
	Button,
	Typography,
	Modal,
	TextField,
	Grid,
	Paper,
	Stack,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { uploadData } from "aws-amplify/storage";
import { createContent, createSeries } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import CSVUploader from "./CSVUploader";
import UploadFile from "./UploadFile";
import FilePreview from "./FilePreview";
import ViewCSVData from "./ViewCSVData";
import { validateCSVHeaders } from "../../helper/csvHeaders";
import AddIcon from "@mui/icons-material/Add";
import { convertToIso6391 } from "../../helper/convertToIso";
import Selector from "../Selector";
import { handleSeriesCreated } from "../../redux/slices/batchContentHandlerSlice";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "auto",
	bgcolor: "background.paper",
	boxShadow: 24,
	maxHeight: "800px",
	overflow: "scroll",
	p: 4,
	borderRadius: "30px",
};

const validationSchema = yup.object({
	title: yup
		.string("Enter the name of the series")
		.required("Name is required"),

	filePath: yup
		.string("Enter the release date")
		.required("Series trailer file is required!"),
});

const ManageSeries = () => {
	const [open, setOpen] = React.useState(false);
	const [isLoading, setLoading] = useState(false);
	const [csvData, setCSVData] = React.useState(null);

	const [file, setFile] = useState(null);

	const [masterFileContent, setMasterFileContent] = React.useState({
		filePath: "",
		file: "",
	});
	const [posterFileContent, setPosterFileContent] = useState({
		filePath: "",
		file: "",
		largeFile: "",
		largeFilePath: "",
	});
	const [isCreatingSeries, setIsCreatingSeries] = useState(false);

	const seriesID = useSelector((state) => state.contentForm.seriesID);
	const agreementForm = useSelector((state) => state.agreementForm);
	const contentForm = useSelector((state) => state.contentForm);

	const client = generateClient();
	const params = useParams();
	const extractDurationNumber = (durationString) => {
		if (!durationString) return 0;
		
		// Use regex to extract only the numeric part
		const matches = durationString.toString().match(/\d+/);
		
		// Return the matched number or 0 if no matches found
		return matches ? parseInt(matches[0], 10) : 0;
	  };

	const numberOfRefreshCycles = agreementForm?.numberOfRefreshCycles;
	let cyclesOptions = [];

	for (let i = 0; i < numberOfRefreshCycles; i++) {
		cyclesOptions.push({ value: i + 1, label: i + 1 });
	}

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);
	const dispatch = useDispatch();
	const formik = useFormik({
		initialValues: {
			title: "",
			plot: "",
			description: "",
			releaseDate: "",
			imdbRating: "",
			rottenRating: "",
			filePath: "",
			rating: "",
			genre: [""],
			duration: 0,
			synopsis: "",
			year: "",
			director: "",
			actors: [""],
			posters: [""],
			seriesId: "",
			currentCycle: params?.currentCycle || 1,
		},
		validationSchema,
		// Update the onSubmit function in the ManageSeries component
onSubmit: async (values) => {
	// Handle series creation
	try {
	  setLoading(true);
	  const response = await client.graphql({
		query: createSeries,
		variables: {
		  input: {
			title: values.title,
		  },
		},
	  });
	  if (response.data.createSeries) {
		await uploadMasterFile();
		await uploadContentData();
		const content = {
		  seriesId: response.data.createSeries.id,
		  title: csvData?.title?.[0]?.value,
		  synopsis: csvData?.description?.[0]?.value,
		  agreementId: agreementForm?.id || "",
		  status: "Pending",
		  type: contentForm.contentType,
		  category: contentForm.category,
		  currentCycle: values?.currentCycle,
		  director: csvData?.director?.[0]?.value,
		  actors: csvData?.actors?.[0]?.values
			?.split(",")
			.map((item) => item.trim()),
		  duration: extractDurationNumber(csvData?.duration?.[0]?.value),
		  language: convertToIso6391(
			csvData?.language?.[0]?.value
		  ),
		  filePath: masterFileContent.filePath,
		  trailerFilePath: "",
		  imdbRating: csvData?.imdbRating?.[0]?.value,
		  rottenRating: csvData?.rottenRating?.[0]?.value,
		  rating: csvData?.rating?.[0]?.value,
		  year: csvData?.year?.[0]?.value,
		  posters: [
			posterFileContent.filePath,
			posterFileContent.largeFilePath,
		  ],
		  genre: csvData?.genre?.[0]?.value
			?.split(",")
			.map((item) => item.trim()),
		  isSeries: true,
		  isDrmProtected: false,
		};
		const creationResponse = await client.graphql({
		  query: createContent,
		  variables: {
			input: content,
		  },
		});
		if (creationResponse.data.createContent) {
		  toast.success("Series has been created!");
		  dispatch(handleSeriesCreated(true));
		}
	  }
	} catch (error) {
	  toast.error("Failed to upload series!");
	  console.error("Error details:", error);
	} finally {
	  setLoading(false);
	}
  },
	});

	const uploadMasterFile = async () => {
		try {
			const filePath = masterFileContent.filePath;
			await uploadData({ key: filePath, data: masterFileContent.file })
				.result;
		} catch (error) {
			console.error(
				"An error occurred during the upload process:",
				error
			);
		}
	};

	const uploadContentData = async () => {
		const posters = [
			{
				filePath: posterFileContent.filePath,
				file: posterFileContent.file,
			},
			{
				filePath: posterFileContent.largeFilePath,
				file: posterFileContent.largeFile,
			},
		];
		try {
			if (posters.length > 0) {
				await Promise.all(
					posters.map(async (poster) => {
						await uploadData({
							key: poster.filePath,
							data: poster.file,
						}).result;
					})
				);
			}
		} catch (error) {
			console.error(
				"An error occurred during the upload process:",
				error
			);
		}
	};

	useEffect(() => {
		if (csvData) {
		  formik.setValues({
			...formik.values,
			rating: csvData?.rating?.[0]?.value || "",
			imdbRating: csvData?.imdbRating?.[0]?.value || "",
			rottenRating: csvData?.rottenRating?.[0]?.value || "",
			title: csvData?.title?.[0]?.value || "",
			genre: csvData?.genre?.[0]?.value
			  ?.split(",")
			  .map((genre) => genre.trim()) || [""],
			duration: extractDurationNumber(csvData?.duration?.[0]?.value) || 0,
			synopsis: csvData?.description?.[0]?.value || "",
			year: csvData?.year?.[0]?.value || "",
			director: csvData?.director?.[0]?.value || "",
			actors: csvData?.actors?.[0]?.value
			  ?.split(",")
			  .map((actor) => actor.trim()) || [""],
		  });
		}
	  }, [csvData]);

	const handleFilePath = (file) => {
		const fileExtension = file.name.split(".").pop().toLowerCase();
		const guid = uuidv4();
		const type = contentForm.contentType;
		const category = contentForm.category;
		const title = csvData[0]?.Title || "";
		const filePath = `${type.replace(/\s+/g, "")}/${category.replace(
			/\s+/g,
			""
		)}/${title.replace(/\s+/g, "")}/${guid}.${fileExtension}`;
		return { filePath };
	};

	const handleFileChange = (event) => {
		const uploadedFile = event.target.files[0];
		if (uploadedFile) {
			setFile(uploadedFile);
			const { filePath } = handleFilePath(uploadedFile);
			if (uploadedFile.type.startsWith("image/")) {
				if (event.target.name === "large") {
					setPosterFileContent({
						...posterFileContent,
						largeFile: uploadedFile,
						largeFilePath: filePath,
					});
				} else {
					setPosterFileContent({
						...posterFileContent,
						file: uploadedFile,
						filePath,
					});
				}
			} else {
				setMasterFileContent({
					file: uploadedFile,
					filePath,
				});
				formik.setFieldValue("filePath", filePath);
			}
		}
	};

	const handleCSVData = (csvData) => {
		const result = validateCSVHeaders(csvData, contentForm?.contentType);
		if (!result?.isValid) {
			result?.errors?.forEach((error) => {
				toast.warning(error);
			});
		}
		setCSVData(result?.mappedHeaders);
	};

	return (
		<>
			<Button
				sx={{ flexGrow: 1, flexShrink: 0, height: "60px" }}
				variant="contained"
				onClick={handleOpen}
			>
				<AddIcon />
				Add Series
			</Button>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-title"
				aria-describedby="modal-description"
			>
				<Box sx={style}>
					<Typography
						id="modal-title"
						variant="h6"
						component="h2"
						textAlign={"center"}
						my={2}
					>
						Create a new series
					</Typography>
					<form onSubmit={formik.handleSubmit}>
						<Grid container spacing={2}>
							<Stack
								direction={"row"}
								sx={{ width: "100%" }}
								gap={4}
								justifyContent={"space-between"}
								alignItems={"center"}
							>
								<Grid flexBasis={"50%"} item xs={12}>
									<CSVUploader
										onFileLoad={(data) =>
											handleCSVData(data)
										}
									/>
								</Grid>
								{csvData && (
									<Grid flexBasis={"50%"} item xs={12}>
										<ViewCSVData csvData={csvData} />
									</Grid>
								)}
							</Stack>
							<Grid item xs={12}>
								<TextField
									fullWidth
									name="title"
									label="Title"
									value={formik.values.title}
									onChange={formik.handleChange}
									error={
										formik.touched.title &&
										Boolean(formik.errors.title)
									}
									helperText={
										formik.touched.title &&
										formik.errors.title
									}
								/>
							</Grid>
							{/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="plot"
                  label="Plot"
                  value={formik.values.plot}
                  onChange={formik.handleChange}
                  error={formik.touched.plot && Boolean(formik.errors.plot)}
                  helperText={formik.touched.plot && formik.errors.plot}
                />
              </Grid> */}
							<Grid item xs={12}>
								<Selector
									label={"Select the current cycle"}
									name={"currentCycle"}
									formik={formik}
									noneValue={"Select current cycle"}
									options={cyclesOptions}
								/>
							</Grid>
							{/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Grid> */}
							{/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="releaseDate"
                  label="Release Date"
                  type="date"
                  value={formik.values.releaseDate}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.releaseDate &&
                    Boolean(formik.errors.releaseDate)
                  }
                  helperText={
                    formik.touched.releaseDate && formik.errors.releaseDate
                  }
                />
              </Grid> */}
							<Grid item xs={12}>
								<Typography variant="h6" gutterBottom>
									Upload Series Poster
								</Typography>
								<Stack direction={"row"} gap={2}>
									<UploadFile
										name={"default"}
										label={"Upload Trailer File"}
										accept="video/*"
										handleFileChange={handleFileChange}
									/>
									<UploadFile
										name={"default"}
										label={"Upload Vertical File"}
										accept="image/*"
										handleFileChange={handleFileChange}
									/>
									<UploadFile
										name={"large"}
										accept="image/*"
										label={"Upload Horizontal Poster"}
										handleFileChange={handleFileChange}
									/>
								</Stack>
							</Grid>
							<Grid item xs={12}>
								<Stack direction={"row"} gap={2}>
									{masterFileContent.filePath && (
										<Box>
											<FilePreview
												file={masterFileContent.file}
											/>
										</Box>
									)}
									{posterFileContent.filePath && (
										<Box height={300}>
											<FilePreview
												file={posterFileContent.file}
											/>
										</Box>
									)}
									{posterFileContent.largeFilePath && (
										<Box>
											<FilePreview
												file={
													posterFileContent.largeFile
												}
											/>
										</Box>
									)}
								</Stack>
							</Grid>

							<Grid item xs={12}>
								<LoadingButton
									type="submit"
									variant="contained"
									color="primary"
									sx={{
										backgroundColor: "primary.darker",
										color: "secondary.main",
									}}
									loading={isLoading}
								>
									Create Series
								</LoadingButton>
							</Grid>
							<Grid item xs={12}>
								{formik?.touched?.filePath &&
									formik?.errors?.filePath && (
										<Typography sx={{ color: "red" }}>
											{formik?.errors?.filePath}
										</Typography>
									)}
							</Grid>
						</Grid>
					</form>
				</Box>
			</Modal>
		</>
	);
};

export default ManageSeries;
